import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UploadIcon from '@mui/icons-material/Upload';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { BookDto } from '../../api/queries/books';
import type { EntitySummaryDto } from '../../api/queries/entities';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CommandItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
  category: 'navigation' | 'action' | 'ai' | 'search';
  action: () => void;
}

/* ------------------------------------------------------------------ */
/*  Recent commands persistence                                        */
/* ------------------------------------------------------------------ */

const RECENT_KEY = 'command_palette_recent';
const MAX_RECENT = 5;

function getRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as string[];
    return [];
  } catch {
    return [];
  }
}

function pushRecent(id: string) {
  try {
    const ids = getRecentIds().filter((r) => r !== id);
    ids.unshift(id);
    localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Keyboard trigger: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Static command items
  const staticItems = useMemo<CommandItem[]>(() => {
    const nav = (
      id: string,
      label: string,
      icon: React.ReactNode,
      to: string,
      shortcut?: string,
    ): CommandItem => ({
      id,
      label,
      icon,
      shortcut,
      category: 'navigation',
      action: () => void navigate({ to }),
    });

    return [
      // Navigation
      nav(
        'nav-dashboard',
        'Go to Dashboard',
        <DashboardIcon fontSize="small" />,
        '/',
      ),
      nav(
        'nav-books',
        'Go to Books',
        <MenuBookIcon fontSize="small" />,
        '/books',
      ),
      nav(
        'nav-entities',
        'Go to Entities',
        <PeopleIcon fontSize="small" />,
        '/entities',
      ),
      nav(
        'nav-worlds',
        'Go to Worlds',
        <PublicIcon fontSize="small" />,
        '/worlds',
      ),
      nav(
        'nav-story-architect',
        'Go to Story Architect',
        <TimelineIcon fontSize="small" />,
        '/story-architect',
      ),
      nav(
        'nav-outlines',
        'Go to Outlines',
        <AccountTreeIcon fontSize="small" />,
        '/outlines',
      ),
      nav(
        'nav-boards',
        'Go to Boards',
        <ViewKanbanIcon fontSize="small" />,
        '/boards',
      ),
      nav(
        'nav-story-studio',
        'Go to Story Studio',
        <AutoStoriesIcon fontSize="small" />,
        '/story-studio',
      ),
      nav(
        'nav-series',
        'Go to Series',
        <MenuBookIcon fontSize="small" />,
        '/series',
      ),
      nav(
        'nav-media-library',
        'Go to Media Library',
        <PhotoLibraryIcon fontSize="small" />,
        '/images',
      ),
      nav(
        'nav-settings',
        'Go to Settings',
        <SettingsIcon fontSize="small" />,
        '/settings',
      ),
      // Actions
      {
        id: 'action-new-book',
        label: 'Create New Book',
        icon: <AddIcon fontSize="small" />,
        category: 'action',
        action: () => void navigate({ to: '/books' }),
      },
      {
        id: 'action-new-entity',
        label: 'Create New Entity',
        icon: <AddIcon fontSize="small" />,
        category: 'action',
        action: () => void navigate({ to: '/entities' }),
      },
      {
        id: 'action-new-world',
        label: 'Create New World',
        icon: <AddIcon fontSize="small" />,
        category: 'action',
        action: () => void navigate({ to: '/worlds' }),
      },
      {
        id: 'action-upload-image',
        label: 'Upload Image',
        icon: <UploadIcon fontSize="small" />,
        category: 'action',
        action: () => void navigate({ to: '/images' }),
      },
      // AI
      {
        id: 'ai-generate-image',
        label: 'Generate Image',
        icon: <ImageIcon fontSize="small" />,
        shortcut: '\u2318R',
        category: 'ai',
        action: () => void navigate({ to: '/images' }),
      },
      {
        id: 'ai-generate-audio',
        label: 'Generate Audio',
        icon: <GraphicEqIcon fontSize="small" />,
        category: 'ai',
        action: () => void navigate({ to: '/books' }),
      },
      {
        id: 'ai-expand-paragraph',
        label: 'Expand Paragraph',
        icon: <EditNoteIcon fontSize="small" />,
        category: 'ai',
        action: () => void navigate({ to: '/books' }),
      },
      {
        id: 'ai-rewrite',
        label: 'Rewrite',
        icon: <AutoFixHighIcon fontSize="small" />,
        category: 'ai',
        action: () => void navigate({ to: '/books' }),
      },
    ];
  }, [navigate]);

  // Dynamic items from cached query data
  const dynamicItems = useMemo<CommandItem[]>(() => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    const items: CommandItem[] = [];

    // Books
    const booksData = queryClient.getQueryData<BookDto[]>(['books']);
    if (booksData) {
      for (const book of booksData) {
        if (book.title.toLowerCase().includes(lowerQuery)) {
          items.push({
            id: `book-${book.id}`,
            label: book.title,
            icon: <MenuBookIcon fontSize="small" />,
            category: 'search',
            action: () =>
              void navigate({
                to: '/books/$bookId',
                params: { bookId: book.id },
              }),
          });
        }
      }
    }

    // Entities
    const entitiesData = queryClient.getQueriesData<{
      items: EntitySummaryDto[];
      total: number;
    }>({
      queryKey: ['entities'],
    });
    const seen = new Set<string>();
    for (const [, data] of entitiesData) {
      if (!data?.items) continue;
      for (const entity of data.items) {
        if (seen.has(entity.id)) continue;
        seen.add(entity.id);
        if (entity.name.toLowerCase().includes(lowerQuery)) {
          items.push({
            id: `entity-${entity.id}`,
            label: entity.name,
            icon: <PeopleIcon fontSize="small" />,
            category: 'search',
            action: () =>
              void navigate({
                to: '/entities/$entityId',
                params: { entityId: entity.id },
              }),
          });
        }
      }
    }

    return items;
  }, [query, queryClient, navigate]);

  // Filtered results
  const results = useMemo<CommandItem[]>(() => {
    const lowerQuery = query.toLowerCase().trim();

    if (!lowerQuery) {
      // Show recent commands, then all static items
      const recentIds = getRecentIds();
      const recentItems = recentIds
        .map((id) => staticItems.find((item) => item.id === id))
        .filter((item): item is CommandItem => item !== undefined);
      const remaining = staticItems.filter(
        (item) => !recentIds.includes(item.id),
      );
      return [...recentItems, ...remaining];
    }

    const matchedStatic = staticItems.filter((item) =>
      item.label.toLowerCase().includes(lowerQuery),
    );

    return [...matchedStatic, ...dynamicItems];
  }, [query, staticItems, dynamicItems]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after dialog renders
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Clamp selectedIndex when results change
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(results.length - 1, 0)));
  }, [results.length]);

  const executeItem = useCallback((item: CommandItem) => {
    pushRecent(item.id);
    setOpen(false);
    item.action();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = results[selectedIndex];
        if (item) executeItem(item);
      }
    },
    [results, selectedIndex, executeItem],
  );

  const categoryLabel = (cat: CommandItem['category']): string => {
    switch (cat) {
      case 'navigation':
        return 'Navigation';
      case 'action':
        return 'Actions';
      case 'ai':
        return 'AI';
      case 'search':
        return 'Search Results';
    }
  };

  // Group results by category for display
  const groupedResults = useMemo(() => {
    const groups: {
      category: CommandItem['category'];
      items: { item: CommandItem; globalIndex: number }[];
    }[] = [];
    let currentCategory: CommandItem['category'] | null = null;

    results.forEach((item, idx) => {
      if (item.category !== currentCategory) {
        currentCategory = item.category;
        groups.push({ category: item.category, items: [] });
      }
      groups[groups.length - 1]!.items.push({ item, globalIndex: idx });
    });

    return groups;
  }, [results]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          m: 0,
          maxHeight: '60vh',
          borderRadius: 2,
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.3)' } },
      }}
    >
      <Box sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Type a command..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: { fontSize: 18, py: 1.5 },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            borderBottom: 1,
            borderColor: 'divider',
          }}
        />

        <List
          dense
          sx={{
            maxHeight: 'calc(60vh - 70px)',
            overflow: 'auto',
            py: 1,
          }}
        >
          {results.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No results found
              </Typography>
            </Box>
          )}

          {groupedResults.map((group) => (
            <Box key={group.category}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: 11,
                }}
              >
                {query.trim() === '' && group === groupedResults[0]
                  ? 'Recent'
                  : categoryLabel(group.category)}
              </Typography>
              {group.items.map(({ item, globalIndex }) => (
                <ListItemButton
                  key={item.id}
                  selected={globalIndex === selectedIndex}
                  onClick={() => executeItem(item)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    mb: 0.25,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                  {item.shortcut && (
                    <Chip
                      label={item.shortcut}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: 12, height: 24 }}
                    />
                  )}
                </ListItemButton>
              ))}
            </Box>
          ))}
        </List>

        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            px: 2,
            py: 1,
            display: 'flex',
            gap: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <kbd style={{ fontFamily: 'monospace' }}>&uarr;&darr;</kbd> navigate
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <kbd style={{ fontFamily: 'monospace' }}>Enter</kbd> select
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <kbd style={{ fontFamily: 'monospace' }}>Esc</kbd> close
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
}
